import { useState, useEffect } from 'react';
import { Button, Input, LoadingSpinner, Modal, Select } from '../../../components/ui';
import { intranetApi as api } from '../../../services/intranet-api';
import { api as mainApi } from '../../../services/api-client';

interface Area {
  area_id: string;
  name: string;
  code: string;
  description: string | null;
}

interface Member {
  membership_id: string;
  user_profile_id: string;
  role: 'viewer' | 'editor';
  assigned_at: string;
  first_name: string;
  last_name: string;
  email: string;
}

interface UserProfile {
  profileId: string;
  firstName: string;
  lastName: string;
  personalEmail: string;
}

/**
 * Admin — Gestione Aree Intranet e Membri.
 */
export function IntranetAdminPage() {
  const [areas, setAreas] = useState<Area[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedArea, setSelectedArea] = useState<Area | null>(null);
  const [members, setMembers] = useState<Member[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(false);

  // Create area state
  const [showCreateArea, setShowCreateArea] = useState(false);
  const [newAreaName, setNewAreaName] = useState('');
  const [newAreaCode, setNewAreaCode] = useState('');
  const [newAreaDesc, setNewAreaDesc] = useState('');
  const [creating, setCreating] = useState(false);

  // Add member state
  const [showAddMember, setShowAddMember] = useState(false);
  const [searchUser, setSearchUser] = useState('');
  const [searchResults, setSearchResults] = useState<UserProfile[]>([]);
  const [selectedUser, setSelectedUser] = useState<UserProfile | null>(null);
  const [memberRole, setMemberRole] = useState<'viewer' | 'editor'>('viewer');
  const [addingMember, setAddingMember] = useState(false);

  // Load areas
  useEffect(() => {
    loadAreas();
  }, []);

  const loadAreas = async () => {
    setLoading(true);
    try {
      // Admin endpoint to list all areas (GET on manage-area-members without specific area returns 400,
      // so we use a different approach: list all areas for admin)
      const response = await api.get<{ data: Area[] }>('/admin/areas');
      setAreas(response.data.data);
    } catch {
      setAreas([]);
    } finally {
      setLoading(false);
    }
  };

  const loadMembers = async (areaId: string) => {
    setLoadingMembers(true);
    try {
      const response = await api.get<{ data: Member[] }>(`/admin/areas/${areaId}/members`);
      setMembers(response.data.data);
    } catch (err) {
      console.error('Failed to load members:', err);
      setMembers([]);
    } finally {
      setLoadingMembers(false);
    }
  };

  const handleSelectArea = (area: Area) => {
    setSelectedArea(area);
    loadMembers(area.area_id);
  };

  const handleCreateArea = async () => {
    if (!newAreaName.trim() || !newAreaCode.trim()) return;
    setCreating(true);
    try {
      await api.post('/admin/areas', {
        name: newAreaName.trim(),
        code: newAreaCode.trim().toLowerCase().replace(/\s+/g, '-'),
        description: newAreaDesc.trim() || undefined,
      });
      setShowCreateArea(false);
      setNewAreaName('');
      setNewAreaCode('');
      setNewAreaDesc('');
      await loadAreas();
    } catch (err: any) {
      alert(err.message || 'Errore nella creazione dell\'area');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteArea = async (area: Area) => {
    if (!confirm(`Eliminare l'area "${area.name}"? I file non verranno cancellati da S3.`)) return;
    try {
      await api.delete(`/admin/areas/${area.area_id}`);
      if (selectedArea?.area_id === area.area_id) {
        setSelectedArea(null);
        setMembers([]);
      }
      await loadAreas();
    } catch (err: any) {
      alert(err.message || 'Errore nell\'eliminazione');
    }
  };

  const handleSearchUsers = async () => {
    if (!searchUser.trim()) return;
    try {
      const response = await mainApi.get<{ data: UserProfile[] }>(`/admin/users?search=${encodeURIComponent(searchUser)}&pageSize=10`);
      setSearchResults(response.data.data);
    } catch {
      setSearchResults([]);
    }
  };

  const handleAddMember = async () => {
    if (!selectedUser || !selectedArea) return;
    setAddingMember(true);
    try {
      await api.post(`/admin/areas/${selectedArea.area_id}/members`, {
        userProfileId: selectedUser.profileId,
        role: memberRole,
      });
      setShowAddMember(false);
      setSelectedUser(null);
      setSearchUser('');
      setSearchResults([]);
      await loadMembers(selectedArea.area_id);
    } catch (err: any) {
      alert(err.message || 'Errore nell\'assegnazione');
    } finally {
      setAddingMember(false);
    }
  };

  const handleChangeRole = async (member: Member, newRole: 'viewer' | 'editor') => {
    if (!selectedArea) return;
    try {
      await api.put(`/admin/areas/${selectedArea.area_id}/members/${member.user_profile_id}`, {
        role: newRole,
      });
      await loadMembers(selectedArea.area_id);
    } catch (err: any) {
      alert(err.message || 'Errore nel cambio ruolo');
    }
  };

  const handleRemoveMember = async (member: Member) => {
    if (!selectedArea) return;
    if (!confirm(`Rimuovere ${member.first_name} ${member.last_name} dall'area?`)) return;
    try {
      await api.delete(`/admin/areas/${selectedArea.area_id}/members/${member.user_profile_id}`);
      await loadMembers(selectedArea.area_id);
    } catch (err: any) {
      alert(err.message || 'Errore nella rimozione');
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="intranet-page">
      <div className="page-header">
        <h2>Gestione Intranet</h2>
        <Button variant="primary" size="sm" onClick={() => setShowCreateArea(true)}>
          + Nuova Area
        </Button>
      </div>

      <div className="intranet-admin-layout">
        {/* Left panel: Areas list */}
        <div className="admin-panel admin-panel--areas">
          <h3>Aree</h3>
          {areas.length === 0 ? (
            <p className="text-muted">Nessuna area creata.</p>
          ) : (
            <ul className="area-list">
              {areas.map((area) => (
                <li
                  key={area.area_id}
                  className={`area-list-item ${selectedArea?.area_id === area.area_id ? 'area-list-item--active' : ''}`}
                >
                  <button className="area-list-button" onClick={() => handleSelectArea(area)}>
                    <strong>{area.name}</strong>
                    <span className="text-muted">{area.code}</span>
                  </button>
                  <button className="area-delete-btn" onClick={() => handleDeleteArea(area)} title="Elimina area">
                    🗑️
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Right panel: Members */}
        <div className="admin-panel admin-panel--members">
          {selectedArea ? (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h3>Membri — {selectedArea.name}</h3>
                <Button variant="secondary" size="sm" onClick={() => setShowAddMember(true)}>
                  + Aggiungi Utente
                </Button>
              </div>

              {loadingMembers ? (
                <LoadingSpinner />
              ) : members.length === 0 ? (
                <p className="text-muted">Nessun utente assegnato a quest'area.</p>
              ) : (
                <div className="members-table-wrapper">
                <table className="members-table">
                  <thead>
                    <tr>
                      <th>Nome</th>
                      <th>Email</th>
                      <th>Ruolo</th>
                      <th>Azioni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {members.map((m) => (
                      <tr key={m.membership_id}>
                        <td>{m.first_name} {m.last_name}</td>
                        <td>{m.email}</td>
                        <td>
                          <select
                            value={m.role}
                            onChange={(e) => handleChangeRole(m, e.target.value as 'viewer' | 'editor')}
                            className="role-select"
                          >
                            <option value="viewer">Viewer</option>
                            <option value="editor">Editor</option>
                          </select>
                        </td>
                        <td>
                          <Button variant="ghost" size="sm" onClick={() => handleRemoveMember(m)}>
                            ❌
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              )}
            </>
          ) : (
            <p className="text-muted">Seleziona un'area per gestire i membri.</p>
          )}
        </div>
      </div>

      {/* Create Area Modal */}
      {showCreateArea && (
        <Modal isOpen={showCreateArea} onClose={() => setShowCreateArea(false)} title="Nuova Area Tematica">
          <div className="modal-form">
            <Input
              label="Nome"
              placeholder="es. Risorse Umane"
              value={newAreaName}
              onChange={(e) => setNewAreaName(e.target.value)}
            />
            <Input
              label="Codice (slug per S3)"
              placeholder="es. risorse-umane"
              value={newAreaCode}
              onChange={(e) => setNewAreaCode(e.target.value.toLowerCase().replace(/\s+/g, '-'))}
            />
            <Input
              label="Descrizione (opzionale)"
              placeholder="Documenti area risorse umane"
              value={newAreaDesc}
              onChange={(e) => setNewAreaDesc(e.target.value)}
            />
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
              <Button variant="ghost" onClick={() => setShowCreateArea(false)}>Annulla</Button>
              <Button variant="primary" onClick={handleCreateArea} disabled={creating || !newAreaName.trim() || !newAreaCode.trim()}>
                {creating ? 'Creazione...' : 'Crea Area'}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Add Member Modal */}
      {showAddMember && (
        <Modal isOpen={showAddMember} onClose={() => { setShowAddMember(false); setSelectedUser(null); setSearchResults([]); }} title="Aggiungi Utente all'Area">
          <div className="modal-form">
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <Input
                label="Cerca utente (nome, cognome, email)"
                placeholder="Cerca..."
                value={searchUser}
                onChange={(e) => setSearchUser(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearchUsers()}
                style={{ flex: 1 }}
              />
              <Button variant="secondary" size="sm" onClick={handleSearchUsers} style={{ alignSelf: 'flex-end', marginBottom: '1rem' }}>
                Cerca
              </Button>
            </div>

            {searchResults.length > 0 && (
              <ul className="user-search-results">
                {searchResults.map((u) => (
                  <li
                    key={u.profileId}
                    className={`user-result ${selectedUser?.profileId === u.profileId ? 'user-result--selected' : ''}`}
                    onClick={() => setSelectedUser(u)}
                  >
                    {u.firstName} {u.lastName} — <span className="text-muted">{u.personalEmail}</span>
                  </li>
                ))}
              </ul>
            )}

            {selectedUser && (
              <div style={{ marginTop: '1rem' }}>
                <p><strong>Utente selezionato:</strong> {selectedUser.firstName} {selectedUser.lastName}</p>
                <Select
                  label="Ruolo"
                  options={[
                    { value: 'viewer', label: 'Viewer (sola lettura)' },
                    { value: 'editor', label: 'Editor (lettura + scrittura)' },
                  ]}
                  value={memberRole}
                  onChange={(e) => setMemberRole(e.target.value as 'viewer' | 'editor')}
                />
              </div>
            )}

            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
              <Button variant="ghost" onClick={() => { setShowAddMember(false); setSelectedUser(null); }}>Annulla</Button>
              <Button variant="primary" onClick={handleAddMember} disabled={addingMember || !selectedUser}>
                {addingMember ? 'Aggiunta...' : 'Aggiungi'}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
